import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import {
  Navigate,
  createBrowserRouter,
  Outlet,
  RouterProvider,
  ScrollRestoration,
} from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import './assets/scss/style.scss';
import SmorgBoardView from './components/recipes/smorg_board_view';
import TopNavBar from './components/user/top_nav_bar';
import ProductsView from './components/shopping_lists/products_view';
import { injectAnalytics } from './operations/google_analytics_operations';
import Env from './env';
import LoginForm from './components/user/login_form';
import ForgotPasswordForm from './components/user/forgot_password_form';
import ResetPasswordForm from './components/user/reset_password_form';
import RequireAuth from './components/common/require_auth';
import './components/common/fontawesome_icons';
import PlannerView from './components/planner/planner_view';
import GlobalSnackbar from './components/common/global_snackbar';
import TopNavBarMobile from './components/user/top_nav_bar_mobile';
import MobileNavigation from './components/user/mobile_navigation';
import UserProfileDesktopView from './components/user/user_profile_desktop_view';
import CreateAccountWizard from './components/onboarding/create_account_wizard';
import { initTracking } from './operations/tracking_operations';
import { submitDeferredIapPurchasesAction } from './action_creators/user_action_creators';
import ModalSpinner from './components/common/modal_spinner';
import { useHasMobileLikeLayout } from './components/common/layout_hooks';
import ProgrammesView from './components/programmes/programmes_view';
import CreatorOnboardingWizard from './components/creator_onboarding/creator_onboarding_wizard';
import SpaceEditForm from './components/creator_studio/space_edit_form';
import SpaceThemeEditForm from './components/creator_studio/space_theme_edit_form';
import SpaceLandingPageEditForm from './components/creator_studio/space_landing_page_edit_form';
import SpaceSplashPageEditForm from './components/creator_studio/space_splash_page_edit_form';
import {
  SIGNUP_SMORG_COMPANION_ROUTE,
  END_USER_PLANNER_RECOMMENDER_ROUTE,
  LOGIN_ROUTE,
  MY_DAY_CONTENT_ENTRY_ROUTE,
  MY_DAY_MEAL_ROUTE,
  MY_DAY_ROUTE,
  PUBLIC_SPACE_LANDING_PAGE_ROUTE,
  SMORG_STUDIO_MEMBERSHIP_TIERS_ROUTE,
  SMORG_STUDIO_MEMBERSHIP_TIER_ROUTE,
  STRIPE_CONNECT_OAUTH_RETURN_URL,
  SMORG_STUDIO_SPACE_EDIT_ROUTE,
  MY_DAY_TODAYS_MEALS_ROUTE,
  END_USER_RECIPES_ROUTE,
  END_USER_RECIPES_BOARD_ROUTE,
  END_USER_RECIPES_BOARD_MEAL_ROUTE,
  END_USER_RECIPES_ADVANCED_SEARCH_ROUTE,
  END_USER_RECIPES_MULTI_MENUS_ROUTE,
  SMORG_STUDIO_SPACE_LANDING_PAGE_EDIT_ROUTE,
  SMORG_STUDIO_SPACE_SPLASH_PAGE_EDIT_ROUTE,
  SMORG_STUDIO_SPACE_MY_DAY_CONTENT_CONFIG_ROUTE,
  END_USER_SIGNUP_ROUTE,
  STRIPE_CHECKOUT_RETURN_URL,
  END_USER_SHARED_PROGRAMMES_ROUTE,
  END_USER_SHARED_PROGRAMME_ROUTE,
  END_USER_SHARED_PROGRAMME_ONBOARD_ROUTE,
  SPACE_ONBOARDING_AFTER_SIGNUP_ROUTE,
  SMORG_COMPANION_START_ROUTE,
  END_USER_PREFERENCES_ACCOUNT_INFORMATION_MOBILE_ROUTE,
  END_USER_PREFERENCES_PERSONAL_INFORMATION_MOBILE_ROUTE,
  END_USER_SHARED_PROGRAMME_PROGRESS_ROUTE,
  MY_DAY_TODAYS_LESSONS_ROUTE,
  PUBLIC_SPACE_LANDING_PAGE_ONBOARD_ROUTE,
  PUBLIC_SPACE_LANDING_PAGE_ONBOARD_SCREEN_ROUTE,
  INITIAL_PROGRAMME_SELECTION_ROUTE,
  END_USER_RECIPE_COLLECTIONS_ROUTE,
  END_USER_RECIPE_SINGLE_COLLECTION_ROUTE,
  END_USER_RECIPE_COLLECTION_MEAL_DETAIL_ROUTE,
  END_USER_RECIPE_COLLECTION_ADD_MEALS_ROUTE,
  END_USER_RECIPE_COLLECTION_SEARCH_MEALS_ROUTE,
  END_USER_RECIPE_COLLECTION_ADD_MEALS_BOARD_ROUTE,
  END_USER_RECIPE_COLLECTION_ADD_MEALS_MULTI_MENUS_ROUTE,
  END_USER_RECIPE_COLLECTION_EDIT_STRUCTURE_ROUTE,
  END_USER_RECIPE_COLLECTION_EDIT_COVER_IMAGE_ROUTE,
  END_USER_FIND_ALTERNATIVES_ROUTE,
  END_USER_FIND_ALTERNATIVES_SEARCH_MEALS_ROUTE,
  END_USER_FIND_ALTERNATIVES_BOARD_ROUTE,
  END_USER_FIND_ALTERNATIVES_MULTI_MENUS_ROUTE,
  END_USER_FIND_ALTERNATIVES_SEARCH_MEALS_TOP_RESULTS_ROUTE,
  END_USER_FIND_ALTERNATIVES_SEARCH_MEALS_GROUP_RESULTS_ROUTE,
  END_USER_FIND_ALTERNATIVES_WITH_MEAL_DETAIL_ROUTE,
  END_USER_FIND_ALTERNATIVES_SEARCH_MEALS_TOP_RESULTS_WITH_MEAL_DETAIL_ROUTE,
  END_USER_FIND_ALTERNATIVES_SEARCH_MEALS_GROUP_RESULTS_WITH_MEAL_DETAIL_ROUTE,
  END_USER_FIND_ALTERNATIVES_MULTI_MENUS_WITH_MEAL_DETAIL_ROUTE,
  END_USER_FIND_ALTERNATIVES_BOARD_WITH_MEAL_DETAIL_ROUTE,
  SMORG_STUDIO_SPACE_SUBSCRIPTION_PAGE_EDIT_ROUTE,
  MY_DAY_SHOPPING_LIST_ROUTE,
  END_USER_RECIPE_SINGLE_COLLECTION_MENU_ROUTE,
  SMORG_STUDIO_SPACE_THEME_EDIT_ROUTE,
  SMORG_STUDIO_SPACE_DESKTOP_BANNER_IMAGE_EDIT_ROUTE,
  END_USER_RECIPES_GRC_RECIPE_CATEGORY_ROUTE,
  END_USER_RECIPES_GRC_RECIPE_ROUTE,
  END_USER_FIND_ALTERNATIVES_GRC_RECIPE_CATEGORY_ROUTE,
  END_USER_FIND_ALTERNATIVES_GRC_RECIPE_ROUTE,
  END_USER_CHAT_ROUTE,
  SMORG_STUDIO_MEMBERSHIPS_ROUTE,
  END_USER_CHAT_GROUP_ROUTE,
  SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARDS_ROUTE,
  SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARD_ROUTE,
  SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARD_ENTRY_ROUTE,
  END_USER_CONTENT_BOARD_ROUTE,
  END_USER_CONTENT_BOARD_LANE_ROUTE,
  SMORG_STUDIO_SPACE_PUBLISHED_SEARCH_TAGS_ROUTE,
  SMORG_STUDIO_SPACE_FAVICON_EDIT_ROUTE,
  SMORG_STUDIO_SPACE_RECIPES_CONFIG_ROUTE,
} from './services/routes';
import SpaceLandingPage from './components/creator_studio/space_landing_page';
import SharedProgrammesView from './components/shared_programmes/shared_programmes_view';
import buildSmorgTheme from './theme';
import EndUserPlannerRecommenderWizard from './components/planner/end_user_planner_recommender_wizard';
import MyDayView from './components/shared_programmes/my_day_view';
import MyDayMealView from './components/shared_programmes/my_day_meal_view';
import MyDayContentView from './components/shared_programmes/my_day_content_view';
import {
  isEmbeddedInSmorgCompanion,
  postMessageToSmorgCompanion,
} from './services/smorg_companion_auth';
import SmorgCompanionSubscribeScreen from './components/onboarding/smorg_companion_subscribe_screen';
import SignupSmorgCompanionScreen from './components/user/signup_smorg_companion_screen';
import MembershipTiersScreen from './components/membership_tiers/membership_tiers_screen';
import SpaceStripeConnectContainer from './components/creator_studio/space_stripe_connect_container';
import MyDayTodaysMealsView from './components/shared_programmes/my_day_todays_meals_view';
import SpaceContentBoardView from './components/creator_studio/space_content_board_view';
import SpaceMyDayContentConfigView from './components/creator_studio/space_my_day_content_config_view';
import SubscribeScreen from './components/onboarding/subscribe_screen';
import StripeCheckoutProcessingScreen from './components/onboarding/stripe_checkout_processing_screen';
import SmorgCompanionStartPage from './components/user/smorg_companion_start_page';
import UserPersonalInformationMobileView from './components/user/user_personal_information_mobile_view';
import SharedProgrammeProgressView from './components/shared_programmes/shared_programme_progress_view';
import MyDayTodaysLessonsView from './components/shared_programmes/my_day_todays_lessons_view';
import SpaceOnboardingAfterSignupContainer from './components/shared_programmes/space_onboarding_after_signup_container';
import SpaceLandingPageSignupOnboardingContainer from './components/creator_studio/space_landing_page_signup_onboarding_container';
import InitialProgrammeSelectionContainer from './components/shared_programmes/initial_programme_selection_container';
import RecipeCollectionsView from './components/recipes/recipe_collections_view';
import RecipeSingleCollectionView from './components/recipes/recipe_single_collection_view';
import RecipeCollectionMealDetailView from './components/recipes/recipe_collection_meal_detail_view';
import RecipeCollectionsAddMealsHomePage from './components/recipes/recipe_collections_add_meals_home_page';
import RecipeCollectionsSearchMealsView from './components/recipes/recipes_collections_search_meals_view';
import RecipeCollectionsAddMealsBoardView from './components/recipes/recipe_collections_add_meals_board_view';
import RecipeCollectionsAddMealsMultiMenusView from './components/recipes/recipe_collections_add_meals_multi_menus_view';
import RecipeCollectionsEditBoardStructureView from './components/recipes/recipe_collections_edit_board_structure_view';
import RecipeCollectionsEditCoverImageView from './components/recipes/recipe_collections_edit_cover_image_view';
import AppLoader from './components/common/app_loader';
import EndUserFindAlternativesHomePage from './components/recipes/end_user_find_alternatives_home_page';
import EndUserFindAlternativesSearchMealsView from './components/recipes/end_user_find_alternatives_search_meals_view';
import EndUserFindAlternativesBoardView from './components/recipes/end_user_find_alternatives_board_view';
import EndUserFindAlternativesMultiMenusView from './components/recipes/end_user_find_alternatives_multi_menus_view';
import SpaceSubscriptionPageEditForm from './components/creator_studio/space_subscription_page_edit_form';
import MyDayShoppingListView from './components/shared_programmes/my_day_shopping_list_view';
import RecipeSingleCollectionMenuView from './components/recipes/recipe_single_collection_menu_view';
import SpaceDesktopBannerImageEditForm from './components/creator_studio/space_desktop_banner_image_edit_form';
import RecipesEndUserGRCRecipeCategoryView from './components/recipes/recipes_end_user_grc_recipe_category_view';
import RecipesEndUserGRCRecipeDetailView from './components/recipes/recipes_end_user_grc_recipe_detail_view';
import EndUserFindAlternativesGRCRecipeCategoryView from './components/recipes/end_user_find_alternatives_grc_recipe_category_view';
import EndUserFindAlternativesGRCRecipeDetailView from './components/recipes/end_user_find_alternatives_grc_recipe_detail_view';
import EndUserChatView from './components/chat/end_user_chat_view';
import UserProfileView from './components/user/user_profile_view';
import SmorgStudioMembershipsView from './components/creator_studio/smorg_studio_memberships_view';
import { DEFAULT_SPACE_ID } from './services/spaces';
import EndUserContentBoardView from './components/shared_programmes/end_user_content_board_view';
import EndUserContentBoardLaneView from './components/shared_programmes/end_user_content_board_lane_view';
import SpacePublishedSearchTagsView from './components/creator_studio/space_published_search_tags_view';
import { userIsCreator } from './services/users';
import { isPaidAndActive } from './services/end_user_space_memberships';
import SpaceFaviconEditForm from './components/creator_studio/space_favicon_edit_form';
import EndUserSharedProgrammeOnboardingView from './components/shared_programmes/end_user_shared_programme_onboarding_view';
import { appLoadedAction } from './features/user_id/user_id_slice';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetEndUserSpaceQuery,
  useGetUserProfileQuery,
} from './app/smorgAppSyncGraphQLApiCustomHooks';
import StaleAppVersionModal from './components/common/stale_app_version_modal';
import {
  checkUpdatedBundleVersion,
  doesAppNeedUpdate,
  refreshAppPreservingBackgroundColor,
} from './services/detect_app_update';
import AppUpdateControl from './components/common/app_update_control';
import CommonDOMMessageListener from './components/common/common_dom_message_listener';
import CommonLocationListener from './components/common/common_location_listener';
import EndUserPublishedResourcesControl from './components/common/end_user_published_resources_control';
import { SPACE_REFRESH_INTERVAL_SECONDS } from './services/end_user_published_resources';
import { useGetPublicSpaceQuery } from './app/smorgAppSyncGraphQLApi';
import SpaceRecipesConfigView from './components/creator_studio/space_recipes_config_view';

const AppShell = () => {
  const dispatch = useDispatch();

  const { data: userProfile, isLoading: isUserProfileLoading } =
    useGetUserProfileQuery();

  const isSmorgStudio = userIsCreator(userProfile);

  const { data: currentSpaceMembership, isLoading: spaceMembershipLoading } =
    useGetCurrentSpaceMembershipQuery(undefined, {
      skip: !userProfile || isSmorgStudio,
    });

  const { data: currentSpace, isLoading: isSpaceLoading } =
    useGetEndUserSpaceQuery();

  const spaceIDOverride = useSelector(
    (state) => state.whiteLabel.endUserDesiredSpaceID,
  );

  const actualSpaceIDForTheme =
    spaceIDOverride || currentSpaceMembership?.spaceID;

  const { data: currentSpaceForTheme } = useGetPublicSpaceQuery(
    actualSpaceIDForTheme,
    {
      skip: !actualSpaceIDForTheme,
      refetchOnMountOrArgChange: SPACE_REFRESH_INTERVAL_SECONDS,
    },
  );

  const backgroundColor = currentSpace?.whiteLabelElements?.backgroundColor;

  const globalAppLoadingIndicator = useSelector(
    (state) => state.loader.globalAppLoading,
  );

  const isAppLoading = globalAppLoadingIndicator || isSpaceLoading; /* ||
    isUserProfileLoading ||
    // (!userProfile && isUserProfileFetching) ||
    spaceMembershipLoading ||
    // (!currentSpaceMembership && spaceMembershipFetching) ||
     */

  const isModalSpinnerVisible = useSelector(
    (state) => state.globalSnackbar.modalSpinnerVisible,
  );

  const isCommunityUser = currentSpaceMembership?.spaceID === DEFAULT_SPACE_ID;

  const theme = buildSmorgTheme(currentSpaceForTheme?.whiteLabelElements);

  const hasMobileLikeLayout = useHasMobileLikeLayout();

  // console.log({ appLoadNetworkState, isSmorgStudio });
  const [appNeedsUpdate, setAppNeedsUpdate] = useState(false);

  useEffect(() => {
    if (currentSpaceMembership) {
      dispatch(submitDeferredIapPurchasesAction(currentSpaceMembership));
    }
  }, [dispatch, currentSpaceMembership]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // console.log('Tab is visible');
        // Check for app updates
        checkUpdatedBundleVersion().then((remoteBundleVersion) => {
          if (doesAppNeedUpdate(remoteBundleVersion)) {
            setAppNeedsUpdate(true);
          }
        });
      } /* else {
        console.log('Tab is hidden');
      } */
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <div
        className={classNames([
          'App',
          {
            'smorg-studio': isSmorgStudio,
          },
          { 'end-user-community-app': isCommunityUser },
          { 'end-user-app': hasMobileLikeLayout },
        ])}>
        <div className="centering-wrapper">
          <CommonLocationListener />
          <CommonDOMMessageListener />
          {isAppLoading ? <AppLoader /> : <Outlet />}
          <GlobalSnackbar />
          {isModalSpinnerVisible && <ModalSpinner />}
          <ScrollRestoration
            getKey={(location) => {
              const queryString =
                location.search && location.search.substring(1);
              // console.log({ queryString });
              let scrollCookie = null;
              if (queryString) {
                const urlSearchParams = new URLSearchParams(queryString);
                scrollCookie = urlSearchParams.get('scrollCookie') || null;
              }
              return location.pathname + scrollCookie;
            }}
          />
        </div>
      </div>
      <AppUpdateControl />
      {userProfile && !isSmorgStudio && <EndUserPublishedResourcesControl />}
      {appNeedsUpdate && (
        <StaleAppVersionModal
          onReload={() => refreshAppPreservingBackgroundColor(backgroundColor)}
        />
      )}
    </ThemeProvider>
  );
};

const HomeComponent = () => {
  const { data: userProfile, isLoading: isUserProfileLoading } =
    useGetUserProfileQuery();

  const isSmorgStudio = userIsCreator(userProfile);

  const { data: currentSpaceMembership, isLoading: spaceMembershipLoading } =
    useGetCurrentSpaceMembershipQuery(undefined, {
      skip: !userProfile || isSmorgStudio,
    });

  const hasPaid = isPaidAndActive(currentSpaceMembership);

  const isCommunityUser = currentSpaceMembership?.spaceID === DEFAULT_SPACE_ID;

  const isSmorgCompanion = isEmbeddedInSmorgCompanion();

  const shouldShowOnboarding = userProfile?.showOnboarding;

  const shouldShowSmorgCompanionSubscribeScreen =
    !!userProfile && !spaceMembershipLoading && isSmorgCompanion && !hasPaid;

  const shouldShowStripeSubscribeScreen =
    !!userProfile &&
    !spaceMembershipLoading &&
    !!currentSpaceMembership &&
    !isSmorgCompanion &&
    !isCommunityUser &&
    !isSmorgStudio &&
    !hasPaid;

  // if (appLoadNetworkState.loading) {
  //   return <span>&nbsp;</span>;
  // }
  // if (!appLoadNetworkState.loaded) {
  //   // Something bad happened, it's not loading and it hasn't loaded successfully
  //   console.warn(
  //     'appLoadNetworkState is not loading and not loaded! This should not happen.',
  //   );
  //   return null;
  // }

  if (isUserProfileLoading) {
    return null;
  }

  if (!isSmorgStudio) {
    // End user
    console.log(`Home component rendering for end user`);
    if (
      !shouldShowSmorgCompanionSubscribeScreen &&
      !shouldShowStripeSubscribeScreen &&
      isCommunityUser
    ) {
      console.log(`Home component navigating to recipes route`);
      return <Navigate to={END_USER_RECIPES_ROUTE} replace />;
    }
    if (
      !!userProfile &&
      !!currentSpaceMembership &&
      !shouldShowSmorgCompanionSubscribeScreen &&
      !shouldShowStripeSubscribeScreen &&
      !isCommunityUser
    ) {
      console.log(`Home component navigating to my day route`);
      return <Navigate to={MY_DAY_ROUTE} replace />;
    }
    if (shouldShowSmorgCompanionSubscribeScreen) {
      return (
        <SmorgCompanionSubscribeScreen
          postMessageToSmorgCompanion={postMessageToSmorgCompanion}
        />
      );
    }
    if (shouldShowStripeSubscribeScreen) {
      return <SubscribeScreen />;
    }
  } else {
    // User is creator
    console.log(`Home component rendering for health pro`);
    return (
      <Navigate
        to={
          shouldShowOnboarding ? '/creatorOnboarding' : END_USER_RECIPES_ROUTE
        }
        replace
      />
    );
  }

  return null;
};

const CreatorOnboardingComponent = () => {
  const { data: userProfile } = useGetUserProfileQuery();

  const shouldShowOnboarding = userProfile?.showOnboarding;

  return shouldShowOnboarding ? (
    <CreatorOnboardingWizard />
  ) : (
    <Navigate to={END_USER_RECIPES_ROUTE} />
  );
};

const App = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    injectAnalytics(Env.GA_TRACKING_CODE || 'G-BSC51Q4QFZ');
  }, []);

  useEffect(() => {
    initTracking().then(() => {
      dispatch(appLoadedAction());
    });
  }, [dispatch]);

  // const appUpdateNeeded = useSelector(doesAppNeedUpdateSelector);
  // console.log({ appUpdateNeeded });

  // console.log({ appLoadNetworkState, userIsCreator });

  // if (!userProfileAvailable) {
  //   // Only when user profile is loaded do we know the theme to apply
  //   return null;
  // }

  // if (!isSmorgStudio && !spaceMembershipAvailable) {
  //   // Only when space membership is loaded do we know the space theme to apply
  //   return null;
  // }

  const memoizedRoutesWithinAppShell = useMemo(() => {
    const allUsersRoutes = [
      {
        path: '/',
        index: true,
        element: (
          <RequireAuth>
            <HomeComponent />
          </RequireAuth>
        ),
      },
      {
        path: STRIPE_CHECKOUT_RETURN_URL,
        element: (
          <RequireAuth>
            <StripeCheckoutProcessingScreen />
          </RequireAuth>
        ),
      },
      { path: END_USER_SIGNUP_ROUTE, Component: CreateAccountWizard },
      {
        path: '/creatorSignup',
        element: <CreateAccountWizard isForCreators />,
      },
      { path: LOGIN_ROUTE, element: <LoginForm /> },
      {
        path: SIGNUP_SMORG_COMPANION_ROUTE,
        element: <SignupSmorgCompanionScreen />,
      },
      { path: '/forgotPassword', element: <ForgotPasswordForm /> },
      { path: '/resetPassword', element: <ResetPasswordForm /> },
      ...[
        END_USER_RECIPES_ROUTE,
        END_USER_RECIPES_BOARD_ROUTE,
        END_USER_RECIPES_MULTI_MENUS_ROUTE,
        END_USER_RECIPES_BOARD_MEAL_ROUTE,
        END_USER_RECIPES_ADVANCED_SEARCH_ROUTE,
      ].map((path) => ({
        path,
        element: (
          <RequireAuth>
            <SmorgBoardView />
            <MobileNavigation />
          </RequireAuth>
        ),
      })),
      {
        path: END_USER_RECIPES_GRC_RECIPE_CATEGORY_ROUTE,
        element: (
          <RequireAuth>
            <RecipesEndUserGRCRecipeCategoryView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_RECIPES_GRC_RECIPE_ROUTE,
        element: (
          <RequireAuth>
            <RecipesEndUserGRCRecipeDetailView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      ...['/recipes/share', '/recipes/:recipesBoardId/share'].map((path) => ({
        path,
        element: (
          <RequireAuth>
            <SmorgBoardView showShareModal />
            <MobileNavigation />
          </RequireAuth>
        ),
      })),
      {
        path: '/board/i/:sharedBoardID',
        element: (
          <RequireAuth returnHere>
            <SmorgBoardView showImportShareModal />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: PUBLIC_SPACE_LANDING_PAGE_ROUTE,
        element: <SpaceLandingPage />,
      },
      ...[
        PUBLIC_SPACE_LANDING_PAGE_ONBOARD_ROUTE,
        PUBLIC_SPACE_LANDING_PAGE_ONBOARD_SCREEN_ROUTE,
      ].map((path) => ({
        path,
        element: <SpaceLandingPageSignupOnboardingContainer />,
      })),
    ];

    const creatorRoutes = [
      ...[
        '/programmes',
        '/programmes/create',
        '/programmes/:programmeId',
        '/programmes/:programmeId/edit',
        '/programmes/:programmeId/nutritionConstraints/edit',
        '/programmes/:programmeId/plans/:planId',
        '/programmes/:programmeId/plans/:planId/edit',
        '/programmes/:programmeId/plans/:planId/preview',
        '/programmes/:programmeId/plans/:planId/entry/:entryId',
        // The route below is unlisted i.e. not exposed in the app
        '/programmes/:programmeId/deepDuplicate',
      ].map((path) => ({
        path,
        element: (
          <RequireAuth>
            <TopNavBar />
            <ProgrammesView />
            <MobileNavigation />
          </RequireAuth>
        ),
      })),
      {
        path: '/creatorOnboarding',
        element: (
          <RequireAuth>
            <CreatorOnboardingComponent />
          </RequireAuth>
        ),
      },
      {
        path: SMORG_STUDIO_SPACE_EDIT_ROUTE,
        element: (
          <RequireAuth>
            <TopNavBar />
            <TopNavBarMobile />
            <SpaceEditForm />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: SMORG_STUDIO_SPACE_LANDING_PAGE_EDIT_ROUTE,
        element: (
          <RequireAuth>
            <TopNavBar />
            <TopNavBarMobile />
            <SpaceLandingPageEditForm />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: SMORG_STUDIO_SPACE_SUBSCRIPTION_PAGE_EDIT_ROUTE,
        element: (
          <RequireAuth>
            <TopNavBar />
            <TopNavBarMobile />
            <SpaceSubscriptionPageEditForm />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: SMORG_STUDIO_SPACE_SPLASH_PAGE_EDIT_ROUTE,
        element: (
          <RequireAuth>
            <TopNavBar />
            <TopNavBarMobile />
            <SpaceSplashPageEditForm />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: SMORG_STUDIO_SPACE_FAVICON_EDIT_ROUTE,
        element: (
          <RequireAuth>
            <TopNavBar />
            <TopNavBarMobile />
            <SpaceFaviconEditForm />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: SMORG_STUDIO_SPACE_THEME_EDIT_ROUTE,
        element: (
          <RequireAuth>
            <TopNavBar />
            <TopNavBarMobile />
            <SpaceThemeEditForm />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: SMORG_STUDIO_SPACE_DESKTOP_BANNER_IMAGE_EDIT_ROUTE,
        element: (
          <RequireAuth>
            <TopNavBar />
            <TopNavBarMobile />
            <SpaceDesktopBannerImageEditForm />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      ...[
        SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARDS_ROUTE,
        SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARD_ROUTE,
        SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARD_ENTRY_ROUTE,
      ].map((path) => ({
        path,
        element: (
          <RequireAuth>
            <TopNavBar />
            <TopNavBarMobile />
            <SpaceContentBoardView />
            <MobileNavigation />
          </RequireAuth>
        ),
      })),
      {
        path: SMORG_STUDIO_SPACE_MY_DAY_CONTENT_CONFIG_ROUTE,
        element: (
          <RequireAuth>
            <TopNavBar />
            <TopNavBarMobile />
            <SpaceMyDayContentConfigView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: SMORG_STUDIO_SPACE_RECIPES_CONFIG_ROUTE,
        element: (
          <RequireAuth>
            <TopNavBar />
            <TopNavBarMobile />
            <SpaceRecipesConfigView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: SMORG_STUDIO_SPACE_PUBLISHED_SEARCH_TAGS_ROUTE,
        element: (
          <RequireAuth>
            <TopNavBar />
            <TopNavBarMobile />
            <SpacePublishedSearchTagsView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      ...[
        SMORG_STUDIO_MEMBERSHIP_TIERS_ROUTE,
        SMORG_STUDIO_MEMBERSHIP_TIER_ROUTE,
      ].map((path) => ({
        path,
        element: (
          <RequireAuth>
            <TopNavBar />
            <TopNavBarMobile />
            <MembershipTiersScreen />
            <MobileNavigation />
          </RequireAuth>
        ),
      })),
      {
        path: STRIPE_CONNECT_OAUTH_RETURN_URL,
        element: <SpaceStripeConnectContainer />,
      },
      {
        path: SMORG_STUDIO_MEMBERSHIPS_ROUTE,
        element: <SmorgStudioMembershipsView />,
      },
    ];

    const endUserOnlyRoutes = [
      {
        path: SMORG_COMPANION_START_ROUTE,
        element: <SmorgCompanionStartPage />,
      },
      {
        path: END_USER_RECIPE_COLLECTIONS_ROUTE,
        element: (
          <RequireAuth>
            <RecipeCollectionsView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_RECIPE_SINGLE_COLLECTION_ROUTE,
        element: (
          <RequireAuth>
            <RecipeSingleCollectionView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_RECIPE_SINGLE_COLLECTION_MENU_ROUTE,
        element: (
          <RequireAuth>
            <RecipeSingleCollectionMenuView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_RECIPE_COLLECTION_MEAL_DETAIL_ROUTE,
        element: (
          <RequireAuth>
            <RecipeCollectionMealDetailView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_RECIPE_COLLECTION_ADD_MEALS_ROUTE,
        element: (
          <RequireAuth>
            <RecipeCollectionsAddMealsHomePage />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_RECIPE_COLLECTION_SEARCH_MEALS_ROUTE,
        element: (
          <RequireAuth>
            <RecipeCollectionsSearchMealsView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_RECIPE_COLLECTION_ADD_MEALS_BOARD_ROUTE,
        element: (
          <RequireAuth>
            <RecipeCollectionsAddMealsBoardView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_RECIPE_COLLECTION_ADD_MEALS_MULTI_MENUS_ROUTE,
        element: (
          <RequireAuth>
            <RecipeCollectionsAddMealsMultiMenusView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_RECIPE_COLLECTION_EDIT_COVER_IMAGE_ROUTE,
        element: (
          <RequireAuth>
            <RecipeCollectionsEditCoverImageView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_RECIPE_COLLECTION_EDIT_STRUCTURE_ROUTE,
        element: (
          <RequireAuth>
            <RecipeCollectionsEditBoardStructureView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      ...[
        '/planner',
        '/planner/:startDate',
        '/planner/meal/:mealId',
        '/planner/:startDate/meal/:mealId',
      ].map((path) => ({
        path,
        element: (
          <RequireAuth>
            <TopNavBar mealBasketWidgetEnabled />
            <PlannerView />
            <MobileNavigation />
          </RequireAuth>
        ),
      })),
      {
        path: END_USER_PLANNER_RECOMMENDER_ROUTE,
        element: (
          <RequireAuth>
            <TopNavBar mealBasketWidgetEnabled />
            <EndUserPlannerRecommenderWizard />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      ...[
        '/products',
        '/products/:productsBoardId/shoppingList/:shoppingListId',
      ].map((path) => ({
        path,
        element: (
          <RequireAuth>
            <TopNavBar />
            <TopNavBarMobile screenTitle="Shopping lists" />
            <ProductsView />
            <MobileNavigation />
          </RequireAuth>
        ),
      })),
      {
        path: END_USER_SHARED_PROGRAMMES_ROUTE,
        element: (
          <RequireAuth>
            <SharedProgrammesView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_SHARED_PROGRAMME_ROUTE,
        element: (
          <RequireAuth>
            <SharedProgrammesView />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_SHARED_PROGRAMME_PROGRESS_ROUTE,
        element: (
          <RequireAuth>
            <SharedProgrammeProgressView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_SHARED_PROGRAMME_ONBOARD_ROUTE,
        element: (
          <RequireAuth>
            <TopNavBar />
            <EndUserSharedProgrammeOnboardingView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: SPACE_ONBOARDING_AFTER_SIGNUP_ROUTE,
        element: (
          <RequireAuth>
            <SpaceOnboardingAfterSignupContainer />
          </RequireAuth>
        ),
      },
      {
        path: INITIAL_PROGRAMME_SELECTION_ROUTE,
        element: (
          <RequireAuth>
            <InitialProgrammeSelectionContainer />
          </RequireAuth>
        ),
      },
      {
        path: MY_DAY_ROUTE,
        element: (
          <RequireAuth>
            <MyDayView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: MY_DAY_TODAYS_MEALS_ROUTE,
        element: (
          <RequireAuth>
            <MyDayTodaysMealsView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: MY_DAY_MEAL_ROUTE,
        element: (
          <RequireAuth>
            <MyDayMealView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: MY_DAY_CONTENT_ENTRY_ROUTE,
        element: (
          <RequireAuth>
            <MyDayContentView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: MY_DAY_SHOPPING_LIST_ROUTE,
        element: (
          <RequireAuth>
            <MyDayShoppingListView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: MY_DAY_TODAYS_LESSONS_ROUTE,
        element: (
          <RequireAuth>
            <MyDayTodaysLessonsView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_CONTENT_BOARD_ROUTE,
        element: (
          <RequireAuth>
            <EndUserContentBoardView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_CONTENT_BOARD_LANE_ROUTE,
        element: (
          <RequireAuth>
            <EndUserContentBoardLaneView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      ...[
        END_USER_FIND_ALTERNATIVES_ROUTE,
        END_USER_FIND_ALTERNATIVES_WITH_MEAL_DETAIL_ROUTE,
      ].map((path) => ({
        path,
        element: (
          <RequireAuth>
            <TopNavBar mealBasketWidgetEnabled />
            <EndUserFindAlternativesHomePage />
            <MobileNavigation />
          </RequireAuth>
        ),
      })),
      ...[
        END_USER_FIND_ALTERNATIVES_SEARCH_MEALS_ROUTE,
        END_USER_FIND_ALTERNATIVES_SEARCH_MEALS_TOP_RESULTS_ROUTE,
        END_USER_FIND_ALTERNATIVES_SEARCH_MEALS_TOP_RESULTS_WITH_MEAL_DETAIL_ROUTE,
        END_USER_FIND_ALTERNATIVES_SEARCH_MEALS_GROUP_RESULTS_ROUTE,
        END_USER_FIND_ALTERNATIVES_SEARCH_MEALS_GROUP_RESULTS_WITH_MEAL_DETAIL_ROUTE,
      ].map((path) => ({
        path,
        element: (
          <RequireAuth>
            <EndUserFindAlternativesSearchMealsView />
            <MobileNavigation />
          </RequireAuth>
        ),
      })),
      ...[
        END_USER_FIND_ALTERNATIVES_BOARD_ROUTE,
        END_USER_FIND_ALTERNATIVES_BOARD_WITH_MEAL_DETAIL_ROUTE,
      ].map((path) => ({
        path,
        element: (
          <RequireAuth>
            <EndUserFindAlternativesBoardView />
            <MobileNavigation />
          </RequireAuth>
        ),
      })),
      ...[
        END_USER_FIND_ALTERNATIVES_MULTI_MENUS_ROUTE,
        END_USER_FIND_ALTERNATIVES_MULTI_MENUS_WITH_MEAL_DETAIL_ROUTE,
      ].map((path) => ({
        path,
        element: (
          <RequireAuth>
            <EndUserFindAlternativesMultiMenusView />
            <MobileNavigation />
          </RequireAuth>
        ),
      })),
      {
        path: END_USER_FIND_ALTERNATIVES_GRC_RECIPE_CATEGORY_ROUTE,
        element: (
          <RequireAuth>
            <EndUserFindAlternativesGRCRecipeCategoryView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_FIND_ALTERNATIVES_GRC_RECIPE_ROUTE,
        element: (
          <RequireAuth>
            <EndUserFindAlternativesGRCRecipeDetailView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      ...[END_USER_CHAT_ROUTE, END_USER_CHAT_GROUP_ROUTE].map((path) => ({
        path,
        element: (
          <RequireAuth>
            <EndUserChatView />
            <MobileNavigation />
          </RequireAuth>
        ),
      })),
      {
        path: '/user',
        element: (
          <RequireAuth>
            <TopNavBar />
            <UserProfileView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_PREFERENCES_ACCOUNT_INFORMATION_MOBILE_ROUTE,
        element: (
          <RequireAuth>
            <TopNavBar />
            <UserProfileDesktopView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
      {
        path: END_USER_PREFERENCES_PERSONAL_INFORMATION_MOBILE_ROUTE,
        element: (
          <RequireAuth>
            <TopNavBar />
            <UserPersonalInformationMobileView />
            <MobileNavigation />
          </RequireAuth>
        ),
      },
    ];

    const desktopOnlyRoutes = [
      {
        path: '/planner/:startDate/alternatives/:calendarDay/meal/:mealType/:mealId',
        element: (
          <RequireAuth>
            <TopNavBar mealBasketWidgetEnabled />
            <PlannerView />
          </RequireAuth>
        ),
      },
    ];

    const mobileOnlyRoutes = [];
    const routesWithinAppShell = [...allUsersRoutes];

    routesWithinAppShell.push(...creatorRoutes);
    routesWithinAppShell.push(...endUserOnlyRoutes);

    // if (isMobile) {
    routesWithinAppShell.push(...mobileOnlyRoutes);
    // } else {
    routesWithinAppShell.push(...desktopOnlyRoutes);
    // }
    return routesWithinAppShell;
  }, []);

  const routes = [
    {
      element: <AppShell />,
      children: memoizedRoutesWithinAppShell,
    },
  ];

  const router = createBrowserRouter(routes);

  return <RouterProvider router={router} />;
};

export default App;
